/**
 * @file Builds the trustee profile schema, branching between the
 * disregarded-trust and the regular-trust beneficial-owner inputs.
 */
import { Alert } from "antd";
import type { RadioChangeEvent } from "antd";

import {
  TOOLTIP_PROFILE_DRE,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC,
  BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP,
} from "@components/Domain";
import { W1 } from "@components/Constants";
import { TYPE_EIN, TYPE_RADIO, TYPE_STRING } from "@components/Form";
import { MarkdownAlert, Disclaimer, Markdown } from "@components/Text";
import { getUsAddressSchema, getAddressSchema } from "@components/Address";

import RULES from "./rules";
import computeResidencyInputs from "./computeResidencyInputs";
import computeIsUsCitizenInput from "./computeIsUsCitizenInput";
import getProfileEntityInputsMap from "./getProfileEntityInputsMap";
import {
  getNoEinAlert,
  TITLE_WARNING,
  DISCARDED_IRA_ALERT,
  LABEL_DISREGARDED_IS_US_PERSON,
  TOOLTIP_DISREGARDED_IS_US_PERSON,
  INVALID_BENEFICIAL_OWNER_ENTITY_TYPE_LABEL,
} from "./getProfileEntityOtherSchema";
import type {
  Input,
  Organization,
  UpdateSchema,
  ResidencyLabels,
  FormProfileEntity,
} from "../types";

const LABEL_BENEFICIAL_OWNER_NAME = "Beneficial Owner Name";
const LABEL_BENEFICIAL_OWNER_IS_US_CITIZEN = "Is this beneficial owner a US citizen?";
const LABEL_TRUST_EIN = "Trust EIN";
const LABEL_HAS_EIN = "Does this trust have an EIN?";
const TOOLTIP_DRE_BENEFICIAL_OWNER_NAME = `The beneficial owner of a disregarded
entity is the person (or entity), as shown on its income tax return, where the
disregarded entity's income or loss is reported. For example, it is the single
member of a single member LLC, or the grantor of a grantor trust. If the direct
owner of the entity is also a disregarded entity, please list the first owner
that is not disregarded for federal tax purposes. Please consult your tax
advisor if you are unsure.`;

const WARNING_STYLE = { marginTop: W1, marginBottom: 0 };
const ALERT_STYLE = { marginTop: W1 };

const BENEFICIAL_OWNER_ADDRESS_LABELS = {
  addressCountry: "Beneficial Owner Country",
  streetAddress: "Beneficial Owner Address",
  addressLocality: "Beneficial Owner City",
  usAddressRegion: "Beneficial Owner State",
  addressRegion: "Beneficial Owner Region",
  usPostalCode: "Beneficial Owner ZIP Code",
  postalCode: "Beneficial Owner Postal Code",
};

/**
 * Builds the trust profile inputs, switching between the
 * disregarded-trust and the regular-trust beneficial-owner fields based
 * on the profile's current answers.
 */
const getProfileEntityTrusteeSchema = (
  organization: Organization,
  updateSchema: UpdateSchema,
  profileNameExtraWarning: string | null,
  profile: FormProfileEntity = {},
): Input[] => {
  const { email: organizationEmail } = organization;

  const {
    hasEinNumber,
    beneficialOwnerAddress,
    beneficialOwnerEntityType,
    isDisregardedForTaxPurposes,
    hasSsn,
    isUsPerson,
    isUsCitizen,
  } = profile;

  const inputPropsMap = getProfileEntityInputsMap();

  const isIndividual = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL;
  const isLLC = isDisregardedForTaxPurposes && beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC;
  const isIra = isDisregardedForTaxPurposes && beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;
  const isSetBeneficialType = beneficialOwnerEntityType !== undefined;
  const isNotDisregarded = isDisregardedForTaxPurposes === false;
  const isNotUsPerson = isUsPerson === false;

  const commonInputs: Input[] = [
    {
      name: "name",
      type: TYPE_STRING,
      label: "Trust’s Full Legal Name",
      required: true,
      maxLength: 255,
      itemProps: {
        extra: profileNameExtraWarning
          ? (
            <MarkdownAlert
              text={profileNameExtraWarning}
              type="warning"
              style={WARNING_STYLE}
            />
          )
          : null,
        rules: [
          RULES.ENTITY_NAME,
        ],
      },
      inputProps: {
        /** Refreshes the schema after the trust name changes. */
        onChange: () => updateSchema(),
      },
    },
    {
      name: "isDisregardedForTaxPurposes",
      label: "Is this trust disregarded for federal income tax purposes?",
      required: true,
      itemProps: {
        extra: <Disclaimer text={TOOLTIP_PROFILE_DRE} />,
      },
      inputProps: {
        /** Refreshes the schema after the disregarded-trust answer changes. */
        onChange: () => updateSchema(),
      },
    },
  ];

  let inputs: Input[] = [
    ...commonInputs,
    ...[
      inputPropsMap.hasEinNumber,
      inputPropsMap.beneficialOwnerEntityType,
      inputPropsMap.beneficialOwnerName,
      inputPropsMap.einNumber,
      inputPropsMap.beneficialOwnerAddress,
      inputPropsMap.isUsCitizen,
      inputPropsMap.residenceState,
      inputPropsMap.hasOtherUsResidenceState,
      inputPropsMap.beneficialOwnerSsnNumber,
      inputPropsMap.beneficialOwnerEinNumber,
      inputPropsMap.isUsPerson,
      inputPropsMap.address,
      inputPropsMap.hasSsn,
      inputPropsMap.hasEinNumber,
      inputPropsMap.beneficialOwnerCustodianEinNumber,
      inputPropsMap.beneficialOwnerTaxFilingStatus,
    ],
  ];

  const isNotUsCitizen = isUsCitizen === false;
  const isTin = isNotUsPerson && isNotUsCitizen;

  /** Returns the has-SSN or has-EIN label for the current beneficial owner type. */
  const getHasSsnLabel = () => {
    if (isIndividual) {
      if (isTin) {
        return "Does this beneficial owner have a US TIN?";
      }

      return "Does this beneficial owner have a SSN?";
    }

    if (isIra) {
      return "Does this beneficial owner custodian have an EIN?";
    }

    return "Does this beneficial owner have an EIN?";
  };

  let labels: ResidencyLabels = {
    hasOtherUsResidenceState: "Is the state listed under the address also the beneficial owner's state of residence for tax purposes?",
    residenceState: "Please list the beneficial owner's state of residence",
    isUsPerson: LABEL_DISREGARDED_IS_US_PERSON,
    isUsPersonTooltip: TOOLTIP_DISREGARDED_IS_US_PERSON,
    hasSsn: getHasSsnLabel(),
  };

  if (isDisregardedForTaxPurposes) {
    if (isSetBeneficialType && !isIndividual) {
      profile.isUsCitizen = false;
    }

    const einNumberInput: Input = hasEinNumber
      ? {
        name: "einNumber",
        type: TYPE_EIN,
        label: LABEL_TRUST_EIN,
        required: true,
      }
      : inputPropsMap.einNumber;

    const iraInputs: Input = isIra && hasSsn
      ? {
        name: "beneficialOwnerCustodianEinNumber",
        type: TYPE_EIN,
        label: "Beneficial Owner Custodian EIN",
      }
      : inputPropsMap.beneficialOwnerCustodianEinNumber;

    const llcInputs: Input[] = isLLC
      ? [
        {
          name: "beneficialOwnerTaxFilingStatus",
          type: TYPE_RADIO,
          label: "Tax Filing Status",
          required: true,
          options: Object.keys(BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP)
            .map((value) => ({ value, label: BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP[value] })),
        },
      ]
      : [
        inputPropsMap.beneficialOwnerTaxFilingStatus,
      ];

    const addressInputs: Input = isSetBeneficialType
      ? {
        name: "beneficialOwnerAddress",
        label: "",
        schema: getAddressSchema(updateSchema, [ "profileEntity", "beneficialOwnerAddress" ], beneficialOwnerAddress, {}, BENEFICIAL_OWNER_ADDRESS_LABELS),
      }
      : inputPropsMap.beneficialOwnerAddress;

    const isUsCitizenInput: Input = isIndividual
      ? computeIsUsCitizenInput(LABEL_BENEFICIAL_OWNER_IS_US_CITIZEN, updateSchema)
      : inputPropsMap.isUsCitizen;

    const residencyInputs = computeResidencyInputs(profile, updateSchema, labels, organizationEmail);

    const stateInputs: Input[] = isSetBeneficialType
      ? [
        ...residencyInputs,
      ]
      : [
        inputPropsMap.residenceState,
        inputPropsMap.hasOtherUsResidenceState,
        inputPropsMap.isUsPerson,
        inputPropsMap.hasSsn,
      ];

    const shouldShowSnnInput = isIndividual && hasSsn;

    const shouldShowEinInput = isSetBeneficialType && !isIndividual && hasSsn && !isIra;

    const einInput: Input = shouldShowEinInput
      ? {
        name: "beneficialOwnerEinNumber",
        type: TYPE_EIN,
        required: true,
        label: "Beneficial Owner EIN",
      }
      : inputPropsMap.beneficialOwnerEinNumber;

    const ssnInput: Input = shouldShowSnnInput
      ? {
        name: "beneficialOwnerSsnNumber",
        required: true,
        label: isTin ? "Beneficial Owner ITIN" : "Beneficial Owner SSN",
      }
      : inputPropsMap.beneficialOwnerSsnNumber;

    /**
     * Refreshes the schema after the beneficial owner's entity type
     * changes, warning when it is an IRA that must go through the
     * SDIRA flow instead.
     */
    const onBeneficialOwnerEntityTypeChange = (event: RadioChangeEvent) => {
      const { value } = event.target;
      const isDisregardedIRA = value === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

      if (isDisregardedIRA) {
        return updateSchema(DISCARDED_IRA_ALERT, "beneficialOwnerEntityType");
      }

      return updateSchema();
    };

    inputs = [
      ...commonInputs,
      ...[
        inputPropsMap.address,
        {
          name: "hasEinNumber",
          label: LABEL_HAS_EIN,
          required: true,
          inputProps: {
            /** Refreshes the schema after the has-EIN answer changes. */
            onChange: () => updateSchema(),
          },
        },
        einNumberInput,
        {
          name: "beneficialOwnerEntityType",
          type: TYPE_RADIO,
          label: "Beneficial Owner Entity Type",
          required: true,
          options: Object
            .keys(ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP)
            .map((value) => ({ value, label: ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP[value] })),
          itemProps: {
            rules: [
              {
                /** Rejects an IRA beneficial owner entity type. */
                validator: async (_: unknown, value: string) => {
                  const isIRA = value === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

                  if (isIRA) {
                    throw new Error(INVALID_BENEFICIAL_OWNER_ENTITY_TYPE_LABEL);
                  }
                },
              },
            ],
          },
          inputProps: {
            onChange: onBeneficialOwnerEntityTypeChange,
          },
        },
        {
          name: "beneficialOwnerName",
          label: LABEL_BENEFICIAL_OWNER_NAME,
          type: TYPE_STRING,
          itemProps: {
            extra: <Disclaimer text={TOOLTIP_DRE_BENEFICIAL_OWNER_NAME} />,
          },
          required: true,
        },
        ...llcInputs,
        addressInputs,
        isUsCitizenInput,
      ],
      ...stateInputs,
      ...[
        einInput,
        ssnInput,
        iraInputs,
      ],
    ];
  }

  if (isNotDisregarded) {
    const noEinAlertMessage = getNoEinAlert(organizationEmail);

    const noEinExtra = hasEinNumber === false
      ? (
        <Alert
          showIcon
          type="warning"
          style={ALERT_STYLE}
          message={TITLE_WARNING}
          description={<Markdown text={noEinAlertMessage} />}
        />
      )
      : undefined;

    labels = {
      hasOtherUsResidenceState: "Is the state listed under the address also the trust state of residence for tax purposes?",
      residenceState: "Please list the trust state of residence",
      isUsPerson: "Is this trust considered a US person for federal tax purposes, as defined by the IRS?",
      hasEinNumber: LABEL_HAS_EIN,
    };

    profile.isUsCitizen = false;

    const residencyInputs = computeResidencyInputs(profile, updateSchema, labels, organizationEmail);

    const einNumberInput: Input = hasEinNumber
      ? {
        name: "einNumber",
        type: TYPE_EIN,
        label: LABEL_TRUST_EIN,
        required: true,
      }
      : inputPropsMap.einNumber;

    const stateInputs: Input[] = [
      ...residencyInputs,
      inputPropsMap.hasSsn,
    ];

    inputs = [
      ...commonInputs,
      inputPropsMap.beneficialOwnerTaxFilingStatus,
      ...[
        {
          name: "address",
          label: "",
          schema: getUsAddressSchema(beneficialOwnerAddress),
          required: true,
        },
      ],
      ...stateInputs,
      {
        name: "hasEinNumber",
        label: LABEL_HAS_EIN,
        required: true,
        itemProps: {
          extra: noEinExtra,
        },
        inputProps: {
          /** Refreshes the schema after the has-EIN answer changes. */
          onChange: () => updateSchema(),
        },
      },
      ...[ einNumberInput ],
      ...[
        inputPropsMap.beneficialOwnerEntityType,
        inputPropsMap.beneficialOwnerName,
        inputPropsMap.beneficialOwnerAddress,
        inputPropsMap.isUsCitizen,
        inputPropsMap.beneficialOwnerTaxFilingStatus,
        inputPropsMap.beneficialOwnerSsnNumber,
        inputPropsMap.beneficialOwnerEinNumber,
        inputPropsMap.beneficialOwnerCustodianEinNumber,
      ],
    ];
  }

  return inputs;
};

export default getProfileEntityTrusteeSchema;
